import React, { CSSProperties, PropsWithChildren, Suspense } from "react";
import { LimaImage, getImageDataOrPublicURL } from "../lima-image";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

export type CardProps = {
  image?: ImageDataLikeWithPropsForGifs | null;
  id?: string | null;
  url?: string | null;
  title?: string | null;
  label?: string | null;
  labelRight?: string | null;
  labelBottom?: string | null;
  border?: boolean | null;
  highlighted?: boolean | null;
  i?: number;
  columns?: number;
  external?: boolean;
  summary?: string | null;
  labelOverride?: string | null;
  background?: boolean | null;
  imageURL?: string | null;
  keywords?: (string | null)[] | null;
  wide?: boolean;
  high?: boolean;
  noImage?: boolean;
  spacer?: boolean;
  objectFit?: CSSProperties["objectFit"];
};

export const Card: React.FC<CardProps & PropsWithChildren> = ({
  image,
  url,
  label,
  labelRight,
  labelBottom,
  title,
  border = true,
  highlighted,
  i = 0,
  id,
  columns = 4,
  external = true,
  summary,
  labelOverride,
  background = true,
  keywords,
  imageURL,
  wide = false,
  high = false,
  noImage = false,
  spacer = false,
  objectFit,
  children,
  ...props
}) => {
  const imageData = image ? getImageDataOrPublicURL(image) : null;
  let paddingClass = "";
  const borderClass = !spacer && border ? "border" : "";
  const colClass = wide ? "xl:col-span-2" : "lg:col-span-2 xl:col-span-1";
  const rowClass = high ? "row-span-2" : "";
  const bgClass = spacer ? "" : "bg-white";
  const urlIsExternal = external || (url && url?.startsWith("http"));
  return (
    <div
      key={id || `card-${i}-${title}`}
      className={`col-span-4 ${colClass} ${rowClass} ${paddingClass} ${
        spacer ? "hidden xl:block" : ""
      }`}
      {...props}
    >
      <div
        className={`group block h-full overflow-hidden rounded border-beige ${
          background ? (highlighted ? "bg-purple text-white" : bgClass) : ""
        } ${borderClass}`}
      >
        <a
          href={url ? url : undefined}
          target={urlIsExternal ? "_blank" : "_self"}
        >
          {!noImage && (
            <div className={`aspect-video w-full overflow-hidden `}>
            {summary && !wide && (
                <div
                className={`h-0 overflow-hidden transition-all delay-500 group-hover:h-full ${bgClass}`}
                >
                  <div
                    className={`max-h-full px-3 py-3 text-lg text-purple lg:text-base
                `}
                  >
                    <span className="line-clamp-4">{summary}</span>
                  </div>
                </div>
              )}
              {(imageData || imageURL) && (
                <LimaImage
                  className="h-full w-full"
                  image={imageData || { publicURL: imageURL, imageData: null }}
                  objectFit={objectFit}
                  alt=""
                  cover={objectFit !== "contain"}
                />
              )}
            </div>
          )}
          {(label ||
            title ||
            labelRight ||
            labelOverride ||
            spacer ||
            urlIsExternal) &&
            border && (
              <div
                className={`border-beige lg:min-h-[7rem] ${
                  wide
                    ? "px-6 pb-8 pt-6"
                    : "px-6 pb-8 pt-6 lg:px-4 lg:pb-4 lg:pt-2"
                } ${!spacer && !noImage ? "border-t" : ""}`}
              >
                <div
                  className={`flex justify-between  text-beige ${
                    wide
                      ? "min-h-10 text-m lg:h-12 lg:text-lg"
                      : "min-h-10 lg:min-h-8 xl:min-h-6 text-m lg:text-m lg:text-sm xl:text-m"
                  } ${
                    labelOverride || labelRight || label ? "" : "hidden lg:flex"
                  }`}
                >
                  <div className="">{label}</div>
                  <div className="">
                    {labelOverride ? labelOverride : labelRight}
                  </div>
                  {urlIsExternal && !spacer && (
                    <ArrowTopRightOnSquareIcon className="h-10 lg:h-8 xl:h-6" />
                  )}
                </div>
                <div
                  className={`line-clamp-2 font-bold leading-tight ${
                    wide
                      ? "mb-3 mt-1 text-lg lg:text-2xl"
                      : "text-lg lg:mb-4 lg:text-lg xl:text-base"
                  }`}
                >
                  {title}
                </div>
                {summary && wide && (
                  <div className="line-clamp-2 text-m text-beige lg:text-lg">
                    <div className="">{summary}</div>
                  </div>
                )}
                {labelBottom && <div className="mt-4">{labelBottom}</div>}
              </div>
            )}
        </a>
        {children}
      </div>
    </div>
  );
};
